import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import backgroundImage from '../assets/Images/mobile/bkgd photo.jpg';
import cross from '../assets/icons/Cross.svg';
import * as actionTypes from '../constantes/actionTypes';
import { MICROSITE_LANGUAGE_PANEL_OPTIONS, MICROSITE_TR_LANGUAGE_PANEL_OPTIONS, MicrositeMarketLanguage } from '../constantes/micrositeMarkets';
import { sendMicrositeCountrySelectedAnalytics, sendMicrositeDutyFreeSelectedAnalytics } from '../constantes/utilitaries';
import i18n from '../i18nextInit';
import { StepView } from '../type';

const MicrositeIntro = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [selectedExperience, setSelectedExperience] = useState<boolean | null>(null);
	const [isDutyFree, setDutyFree] = useState<boolean | null>(null);

	const openLanguageSelector = (isShadematch: boolean) => {
		setSelectedExperience(isShadematch);
		setDutyFree(null);
	};

	const closeLanguageSelector = () => {
		setSelectedExperience(null);
		setDutyFree(null);
	};

	const selectDutyFree = (value: boolean) => {
		sendMicrositeDutyFreeSelectedAnalytics(value);
		setDutyFree(value);
	};

	const selectLanguage = (languageConfig: MicrositeMarketLanguage) => {
		const isShadematch = selectedExperience === true;

		i18n.changeLanguage(languageConfig.language);
		document.body.style.direction = window.RTL_LANGS.includes(languageConfig.language) ? 'rtl' : 'ltr';
		sendMicrositeCountrySelectedAnalytics(languageConfig.locale);
		dispatch({ type: actionTypes.SET_MARKET, value: languageConfig.market });
		dispatch({ type: actionTypes.SET_LANGUAGE, value: languageConfig.language });
		dispatch({ type: actionTypes.SET_SHADEMATCH, value: isShadematch });
		dispatch({
			type: actionTypes.SWITCH_STEP,
			value: isShadematch ? StepView.shadematchPrivacy : StepView.onBoard,
		});
	};

	return (
		<Container initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
			<Background src={backgroundImage} alt='' />
			<Content>
				<Title>{t('diagnostic-tool-title')}</Title>
				<Buttons>
					<ChoiceButton whileTap={{ scale: 0.98 }} onClick={() => openLanguageSelector(true)}>
						{t('microsite-intro-shadematch')}
					</ChoiceButton>
					<ChoiceButton whileTap={{ scale: 0.98 }} onClick={() => openLanguageSelector(false)}>
						{t('analyze-your-skin')}
					</ChoiceButton>
				</Buttons>
			</Content>
			<AnimatePresence>
				{selectedExperience !== null && (
					<ModalOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.28, ease: 'easeOut' }} onClick={closeLanguageSelector}>
						<LanguageDialog
							layout
							initial={{ opacity: 0, scale: 0.97, y: 18 }}
							animate={{ opacity: 1, scale: 1, y: 0 }}
							exit={{ opacity: 0, scale: 0.985, y: 10 }}
							transition={{ duration: 0.34, ease: [0.22, 1, 0.36, 1], layout: { duration: 0.72, ease: [0.16, 1, 0.3, 1] } }}
							role='dialog'
							aria-modal='true'
							aria-label='Select language'
							onClick={(event) => event.stopPropagation()}>
							<CloseButton
								type='button'
								aria-label='Close'
								onClick={(event) => {
									event.stopPropagation();
									closeLanguageSelector();
								}}>
								<CloseIcon src={cross} alt='' aria-hidden='true' />
							</CloseButton>
							<AnimatePresence mode='wait' initial={false}>
								{isDutyFree === null ? (
									<PanelContent key='duty-free' initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.24, ease: 'easeOut' }}>
										<DutyFreePanel>
											<DutyFreeQuestion>Are you shopping in a duty-free store?</DutyFreeQuestion>
											<DutyFreeButtons>
												<DutyFreeButton type='button' onClick={() => selectDutyFree(true)}>Yes</DutyFreeButton>
												<DutyFreeButton type='button' onClick={() => selectDutyFree(false)}>No</DutyFreeButton>
											</DutyFreeButtons>
										</DutyFreePanel>
									</PanelContent>
								) : (
									<PanelContent key={isDutyFree ? 'travel-retail-languages' : 'standard-languages'} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.34, ease: 'easeOut' }}>
									<LanguageGrid $isTravelRetail={isDutyFree}>
										{(isDutyFree ? MICROSITE_TR_LANGUAGE_PANEL_OPTIONS : MICROSITE_LANGUAGE_PANEL_OPTIONS).map((languageConfig) => (
											<LanguageButton key={`${languageConfig.locale}-${languageConfig.market}`} type='button' $isTravelRetail={isDutyFree} aria-label={`${languageConfig.country} - ${languageConfig.label}`} onClick={() => selectLanguage(languageConfig)}>
												<LanguageName $isTravelRetail={isDutyFree}>{languageConfig.displayName}</LanguageName>
												<Flag aria-hidden='true'>{languageConfig.flag}</Flag>
											</LanguageButton>
											))}
										</LanguageGrid>
									</PanelContent>
								)}
							</AnimatePresence>
						</LanguageDialog>
					</ModalOverlay>
				)}
			</AnimatePresence>
		</Container>
	);
};

const Container = styled(motion.main)`
	position: relative;
	box-sizing: border-box;
	width: 100%;
	min-height: 100vh;
	min-height: 100svh;
	overflow: hidden;
	background: #f4f4f4;
`;

const Background = styled.img`
	position: absolute;
	top: -164px;
	left: 0;
	width: 100%;
	height: auto;
	opacity: 1;
	z-index: 0;
`;

const Content = styled.div`
	position: relative;
	z-index: 1;
	box-sizing: border-box;
	display: flex;
	min-height: 100vh;
	min-height: 100svh;
	flex-direction: column;
	justify-content: flex-end;
	padding: 0 20px 74px;
`;

const Title = styled.h1`
	max-width: 680px;
	margin: 0 0 20px;
	font: normal 300 clamp(32px, 7.8vw, 54px) / 0.98 'Clinique Helvetica Neue';
	letter-spacing: -0.035em;
	color: #0b0b0b;
`;

const Buttons = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
`;

const ChoiceButton = styled(motion.button)`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 52px;
	padding: 12px;
	border: 0;
	background: #fff;
	color: #1a1a1a;
	font: normal 400 16px/1.1 'Clinique Helvetica Neue';
	cursor: pointer;

	@media (max-width: 420px) {
		font-size: 14px;
	}
`;

const ModalOverlay = styled(motion.div)`
	position: fixed;
	inset: 0;
	z-index: 20;
	display: flex;
	align-items: center;
	justify-content: center;
	box-sizing: border-box;
	padding: 24px;
	background: rgba(0, 0, 0, 0.42);
`;

const LanguageDialog = styled(motion.div)`
	position: relative;
	box-sizing: border-box;
	width: min(100%, 460px);
	max-height: min(78vh, 720px);
	overflow: visible;
	background: #fafafa;
	color: #1a1a1a;
	box-shadow: 0 18px 60px rgba(0, 0, 0, 0.24);
`;

const PanelContent = styled(motion.div)`
	overflow: hidden;
`;

const CloseButton = styled.button`
	position: absolute;
	top: -46px;
	right: 0;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 36px;
	padding: 0;
	border: 1px solid #cfcfcf;
	border-radius: 50%;
	background: #fff;
	color: #1a1a1a;
	cursor: pointer;
`;

const CloseIcon = styled.img`
	width: 0.9em;
	height: 0.9em;
	object-fit: contain;
	filter: brightness(0);
`;

const DutyFreePanel = styled.div`
	box-sizing: border-box;
	padding: 36px 24px 28px;
	background: #fafafa;
`;

const DutyFreeQuestion = styled.p`
	margin: 0 0 28px;
	text-align: center;
	font: normal 400 24px/1.16 'Clinique Helvetica Neue';
	color: #1a1a1a;
`;

const DutyFreeButtons = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
`;

const DutyFreeButton = styled.button`
	min-height: 52px;
	border: 0;
	background: #000;
	color: #fff;
	font: normal 400 16px/1.1 'Clinique Helvetica Neue';
	cursor: pointer;
	transition: background 160ms ease, transform 160ms ease;

	&:hover,
	&:focus-visible {
		background: #333;
		outline: none;
	}

	&:active {
		transform: scale(0.98);
	}
`;

const LanguageGrid = styled.div<{ $isTravelRetail: boolean }>`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 8px;
	max-height: min(78vh, 720px);
	padding: 8px;
	box-sizing: border-box;
	overflow: auto;
	background: #fafafa;

	@media (max-width: 480px) {
		max-height: min(76vh, 640px);
		gap: 6px;
		padding: 6px;
	}
`;

const LanguageButton = styled.button<{ $isTravelRetail: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	gap: 9px;
	aspect-ratio: 1;
	min-height: 0;
	padding: 10px 6px;
	border: 0;
	background: #fff;
	color: #1a1a1a;
	cursor: pointer;
	transition: background 160ms ease, box-shadow 160ms ease, transform 160ms ease;

	&:hover,
	&:focus-visible {
		background: #fff;
		box-shadow: 0 4px 12px 0 #0000001a;
		transform: translateY(-1px);
		outline: none;
	}

	&:active {
		transform: scale(0.98);
	}

	@media (max-width: 480px) {
		aspect-ratio: auto;
		min-height: ${(props) => (props.$isTravelRetail ? '132px' : '118px')};
		padding: 9px 4px;
		gap: 8px;
	}
`;

const LanguageName = styled.span<{ $isTravelRetail: boolean }>`
	width: 100%;
	min-height: 38px;
	display: flex;
	align-items: flex-end;
	justify-content: center;
	text-align: center;
	white-space: pre-line;
	overflow-wrap: anywhere;
	font: normal 400 ${(props) => (props.$isTravelRetail ? '12px' : '15px')} / 1.12 'Clinique Helvetica Neue';
	color: #1a1a1a;
`;

const Flag = styled.span`
	font-size: clamp(44px, 11vw, 66px);
	line-height: 1;

	@media (max-width: 480px) {
		font-size: 44px;
	}
`;

export default MicrositeIntro;
